import React from "react";
import styles from "./index.module.css";
import { paymentSummaryIdEnums } from "../utils/constant";
import LockIcon from "../../assets/lock_fill.png";
import ProceedButton from "../ProceedButton";
import PayPalCheckout from "../PayPalCheckout";
import {
  isCatalyst,
  catalystTermsUrl,
  catalystPrivacyPolicyUrl,
} from "../../utils/brandConfig";

// TermsAndPrivacy component can be reused in other sections of the checkout page if needed in the future, hence created as a separate component.
export const TermsAndPrivacy = ({ policy, businessUnitId }) => {
  const catalystMode = isCatalyst(businessUnitId);
  const brandName = catalystMode ? "Catalyst's" : "Codeyoung's";
  const termsHref = catalystMode
    ? catalystTermsUrl
    : policy?.terms || "https://www.codeyoung.com/terms";
  const privacyHref = catalystMode
    ? catalystPrivacyPolicyUrl
    : policy?.privacyPolicy || "https://www.codeyoung.com/privacy";

  return (
    <p className={styles.securedInfoText}>
      By proceeding you are agreeing to {brandName}{" "}
      <a
        href={termsHref}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.link}
      >
        terms of use
      </a>{" "}
      and{" "}
      <a
        href={privacyHref}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.link}
      >
        privacy policy
      </a>
    </p>
  );
};

const PaymentSummary = ({
  payment,
  registrationDetails,
  subscribedFor,
  subscriptionDuration,
  subscriptionPeriodInterval,
  convenienceFees,
  currencySymbol,
  vendor,
  policy,
  handleClick,
  // PayPal specific props
  paypalPlanId,
  setError,
  setSuccess,
  submit,
  order,
  isSelfCheckout,
  selfCheckoutDetails,
  browserInfo,
  ipAddress,
  paymentId,
  isFullpayment,
  isPaymentLoading,
}) => {
  const roundHalf = (num) => {
    return (Math.round(num * 2) / 2).toFixed(2);
  };

  const formatSubscriptionFrequency = ({
    period,
    interval = 1,
    isForBlueNote = false,
  }) => {
    if (!period) return "";

    if (isForBlueNote) {
      return interval === 1
        ? period === "monthly"
          ? "every month"
          : period === "weekly"
            ? "every week"
            : period === "yearly"
              ? "every year"
              : `every ${period}`
        : period === "monthly"
          ? `every ${interval} months`
          : period === "weekly"
            ? `every ${interval} weeks`
            : period === "yearly"
              ? `every ${interval} years`
              : `every ${interval} ${period}`;
    }

    if (interval === 1) {
      return period === "monthly"
        ? "month"
        : period === "weekly"
          ? "week"
          : period === "yearly"
            ? "year"
            : period;
    } else {
      return period === "monthly"
        ? `${interval} months`
        : period === "weekly"
          ? `${interval} weeks`
          : period === "yearly"
            ? `${interval} years`
            : `${interval} ${period}`;
    }
  };

  const getSubscriptionDurationLabel = () => {
    if (!subscribedFor) return "";
    if (subscribedFor === "monthly")
      return subscriptionDuration > 1 ? "months" : "month";
    if (subscribedFor === "weekly")
      return subscriptionDuration > 1 ? "weeks" : "week";
    if (subscribedFor === "yearly")
      return subscriptionDuration > 1 ? "years" : "year";
    return "";
  };

  // Calculate amounts with convenience fees if applicable
  const getAmount = (amount) => {
    if (!amount) return "";
    if (convenienceFees > 0) {
      return `${currencySymbol} ${roundHalf((amount * 100) / (100 + convenienceFees))}`;
    }
    return `${currencySymbol} ${Number(amount)?.toFixed(2)}`;
  };

  // Determine if this is registration amount only (Token type payment)
  const isRegistrationOnly = payment?.type === "Token";

  // Show registration amount when it's different from payment amount in subscription
  const showRegistrationAmount =
    subscribedFor && payment?.amount !== registrationDetails?.amount;

  const paymentSummaryDetails = [
    {
      id: paymentSummaryIdEnums?.registrationAmount,
      title: "Registration amount",
      value: getAmount(payment?.amount),
      isVisible: showRegistrationAmount,
    },
    {
      id: paymentSummaryIdEnums?.subscriptionAmount,
      title: "Subscription amount",
      value: getAmount(registrationDetails?.amount),
      duration: `every ${formatSubscriptionFrequency({ period: subscribedFor, interval: subscriptionPeriodInterval })}`,
      isVisible: !!subscribedFor,
    },
    {
      id: paymentSummaryIdEnums?.subscriptionDuration,
      title: "Subscription duration",
      value: `${subscriptionDuration} ${getSubscriptionDurationLabel()}`,
      isVisible: !!subscribedFor,
      subscriptionNote: `Note: Subscription amount is auto debited ${formatSubscriptionFrequency({ period: subscribedFor, interval: subscriptionPeriodInterval, isForBlueNote: true })} for the duration`,
    },
    {
      id: paymentSummaryIdEnums?.totalAmount,
      title: "Total course amount",
      value: getAmount(payment?.amount),
      isVisible: !subscribedFor,
    },
  ];

  const handleButtonClick = (e) => {
    if (handleClick) {
      handleClick(e);
    }
  };

  const buttonText = `Proceed to Pay ${currencySymbol}${Number(payment?.amount)?.toFixed(2)}`;

  return (
    <div className={styles.paymentSummary}>
      <div className={styles.paymentDetails}>
        {paymentSummaryDetails?.map((detail) => {
          if (detail?.isVisible) {
            return (
              <div key={detail?.id} className={styles.detailRow}>
                <span className={styles.detailTitle}>{detail?.title}</span>
                <div className={styles.valueRow}>
                  <span className={styles.detailValue}>{detail?.value}</span>
                  {detail?.duration && (
                    <span className={styles.duration}>{detail?.duration}</span>
                  )}
                </div>
                {detail?.subscriptionNote && (
                  <span className={styles.subscriptionNote}>
                    {detail?.subscriptionNote}
                  </span>
                )}
              </div>
            );
          }
          return null;
        })}
        <div className={styles.proceedButtonContainer}>
          {vendor === "paypal" ? (
            <PayPalCheckout
              plan_id={paypalPlanId || null}
              payment_id={paymentId}
              currency={
                isSelfCheckout
                  ? selfCheckoutDetails?.currencyId
                  : order?.[0]?.currencyId
              }
              setError={setError}
              setSuccess={setSuccess}
              submit={submit}
              order={order}
              payment={payment}
              browserInfo={browserInfo}
              ipAddress={ipAddress}
              isFullpayment={isFullpayment}
              isSelfCheckout={isSelfCheckout}
              selfCheckoutDetails={selfCheckoutDetails}
            />
          ) : (
            <ProceedButton
              text={buttonText}
              onClick={handleButtonClick}
              isLoading={isPaymentLoading}
            />
          )}
        </div>
      </div>
      <div className={styles.securedInfoContainer}>
        <TermsAndPrivacy policy={policy} />
        <div className={styles.securedInfo}>
          <p className={styles.securedInfoText}>Secured and encrypted</p>
          <img src={LockIcon} className={styles.lockIcon} alt="lock icon" />
        </div>
      </div>
    </div>
  );
};

export default PaymentSummary;
